import { Feature, GeoJsonProperties, Geometry, Point, Polygon } from 'geojson';

import { DateBlock, TimeSelectorItem } from '@methanesat/ui-components';

import { CaptureFeatureProperties } from './MapLayers';
import { Platforms } from './MapState';

/**
 * Available STAC Collections (see `/stac/collections` endpoint)
 */
export enum STACCollection {
    MethaneAIR_Level3_Portal = 'MethaneAIR_Level3_Portal',
    MethaneSAT_Level3_Portal = 'MethaneSAT_Level3_Portal',
    MethaneAIR_Level4_Portal = 'MethaneAIR_Level4_Portal',
    MethaneAIR_Level4_point_pruned = 'MethaneAIR_Level4_point_pruned',
    MethaneSAT_Level4_Portal = 'MethaneSAT_Level4_Portal',
    MethaneSAT_Level4_point_pruned = 'MethaneSAT_Level4_point_pruned',
    MethaneSAT_Level4_Portal_twostep = 'MethaneSAT_Level4_Portal_twostep'
}

export function isSTACCollectionId(data: unknown): data is STACCollection {
    if (typeof data !== 'string') return false;
    const ids = Object.values(STACCollection);
    return ids.includes(data as STACCollection);
}
/**
 * Properties returned from stac raster details search
 * in `reducers/api/apiSlice.ts`:
 * `getRasterPointFeature`
 */

/**
 * Raster data.
 * Note: this is specific to L4 data.
 */
export interface RasterPointProperties {
    basin?: string;
    collectionEndTime: string;
    collectionId: STACCollection;
    collectionStartTime: string;
    methane: number;
    methaneLowerBound?: number;
    methaneUpperBound?: number;
    /** prop for two-step data, indicating if the pixel was discarded after the first step */
    pixelDiscarded?: boolean;
    pixelSizeKm2?: number;
    processorVersion: string;
    sceneId: string;
    source: string;
    target_id: string;
    /** used for combined targets, may be the same as original target_id */
    target_group_id?: string;
}

export type RasterPointFeature = GeoJSON.Feature<GeoJSON.Point, RasterPointProperties> & {
    id: string;
    collection: STACCollection;
};

/**
 * Type guard for raster data.
 * Note: this only checks for the properties `methane`, `collectionId`, and `collectionStartTime`,
 * which are required in the format for L4 data.
 */
export function isRasterPointProperties(data: unknown): data is RasterPointProperties {
    if (!data || typeof data !== 'object') return false;
    return 'methane' in data && 'collectionId' in data && 'collectionStartTime' in data;
}

/**
 * L4 plume GeoJSON.
 */
export interface DppPlumeProperties {
    // DPP data
    time_coverage_start: string;
    // DPP data
    time_coverage_end: string;
}
export interface NonDppPlumeProperites {
    // non-DPP data
    start_datetime: string;
    // non-DPP data
    end_datetime: string;
}
export type PlumeProperties<AdditionalSourceTypeProperties = NonDppPlumeProperites> = AdditionalSourceTypeProperties & {
    instrument: string;
    basin: string;
    flux: number;
    flux_lo?: number;
    flux_hi?: number;
    // basin or other human-readable location string
    location: string;
    target_id: string;
    plume_id: number;
};

export function isDPPPlumeProps(props: PlumeProperties<object>): props is PlumeProperties<DppPlumeProperties> {
    return 'flux' in props && 'time_coverage_start' in props && 'time_coverage_end' in props;
}
export type PlumeFeature = GeoJSON.Feature<GeoJSON.Point, PlumeProperties<NonDppPlumeProperites | DppPlumeProperties>>;
export function isPlumeProperties(data: unknown): data is PlumeProperties<NonDppPlumeProperites | DppPlumeProperties> {
    if (!data || typeof data !== 'object') return false;
    // TODO: remove multiple property checks when able
    // currently handling DPP & non-DPP data
    // https://methanesat.atlassian.net/browse/DP-5136
    return (
        'flux' in data &&
        ('start_datetime' in data || 'time_coverage_start' in data) &&
        ('end_datetime' in data || 'time_coverage_end' in data)
    );
}

/**
 * Params required in the app URL to highlight an emissions feature on load
 */
export type SelectedFeatureUrlParams = {
    lat: number;
    lng: number;
    date: number;
    platform: Platforms;
    collectionId: STACCollection;
    itemId: string;
    targetDate: string;
    targetId: string;
};

/**
 * List of Fields to include/exclude.  We exclude fields that Portal doesn't use to make the
 * response more lightweight.
 */
export type StacSearchField = {
    /** fields to include */
    include?: string[];
    /** fields to exclude */
    exclude?: string[];
};

/**
 * STAC sortby options.
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/postItemSearch
 */
export enum SortDirection {
    Ascending = 'asc',
    Decending = 'desc'
}

/**
 * Sort preference for returned STAC search results.
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/postItemSearch
 */
export type StacSearchSorter = {
    /** field: field names to use to sort result */
    field: string;
    /** direction the field should be sorted */
    direction: SortDirection;
};

export type StacFilterProperty = {
    property: string;
};

const isStacFilterProperty = (input: unknown): input is StacFilterProperty => {
    if (input && typeof input === 'object' && 'property' in input && input.property) {
        return typeof input.property === 'string';
    }
    return false;
};

// There are a multitiude of other options that can go here
// These should be added later when/as needed
export type StacFilterValue = string | number | string[] | number[];

const isStacFilterValue = (input: unknown): input is StacFilterValue => {
    if (Array.isArray(input))
        return input.every((v) => typeof v === 'number') || input.every((v) => typeof v === 'string');
    return typeof input === 'string' || typeof input === 'number';
};

export type StacFilterArg = [StacFilterProperty, StacFilterValue];

export const isStacFilterArg = (input: unknown): input is StacFilterArg => {
    if (!Array.isArray(input)) return false;
    if (input.length !== 2) return false;
    return isStacFilterProperty(input[0]) && isStacFilterValue(input[1]);
};

// see https://github.com/stac-api-extensions/filter
export enum StacFilterOperator {
    or = 'OR',
    and = 'AND',
    eq = '=',
    lt = '<',
    lte = '<=',
    gt = '>',
    gte = '>=',
    between = 'BETWEEN',
    not = 'NOT',
    like = 'LIKE',
    temporalIntersection = 't_intersects',
    spatialIntersection = 's_intersects',
    isNull = 'isNull'
}

/**
 * Filter for STAC search.
 * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/postItemSearch
 */
export type StacFilter = {
    // operation, e.g. `=`
    op: StacFilterOperator;
    // arguments, e.g. target_id, mair-anadarko
    args: StacFilterArg | StacFilter[];
};

const isStacKeyValue = (input: unknown) => {
    return Array.isArray(input) && input.length === 2 && isStacFilterProperty(input[0]) && input[1];
};

const isStacOperator = (input: unknown): input is StacFilterOperator => {
    return typeof input === 'string' && Object.values(StacFilterOperator).includes(input as StacFilterOperator);
};
export const isStacFilter = (input: unknown): input is StacFilter => {
    if (typeof input === 'object' && input && 'op' in input && 'args' in input) {
        const { op, args } = input;
        return isStacOperator(op) && Array.isArray(args) && (args.every(isStacFilter) || isStacKeyValue(args));
    }
    return false;
};

/**
 * Request query for STAC search.
 * For more information about STAC properties and structure for searches see:
 * https://github.com/radiantearth/stac-spec/blob/master/item-spec/item-spec.md
 */
export type StacSearch = {
    /** collections to search on */
    collections?: STACCollection[];
    /** ids to search on */
    ids?: string[];
    /** the number of results to return */
    limit?: number;
    /** Bounding box in the form of: min lng, min lat, max lng, max lat */
    bbox?: StacFormattedBbox;
    /** Which fields to return from the search*/
    fields?: StacSearchField;
    /** Filter results by properties and features */
    filter?: StacFilter | string;
    /** language used by the filter */
    'filter-lang'?: StacFilterLang;
    /** Which parameters to sort the results by */
    sortby?: StacSearchSorter[];
    /** Time range to return results from "from_date/to_date"
     * ex: YYYY-MM-DDTHH:mm:SSZ/YYYY-MM-DDTHH:mm:SSZ
     */
    datetime?: string;
    /** Point the search should intersect with */
    intersects?: StacSearchIntersect;
};

/**
 * Link to Stac Item
 */
export type StacItemLink = {
    rel?: string;
    href?: string;
    type?: string;
};

/**
 * Asset definition
 */
export type StacAsset = {
    href: string;
    type: string;
    title?: string;
    roles?: string[];
};

export type StacFeature<G extends GeoJSON.Geometry = GeoJSON.Geometry, P = GeoJsonProperties> = Feature<G, P> & {
    id: string;
    collection: STACCollection;
    stac_version?: string;
    links?: StacItemLink[];
    assets?: {
        COG?: StacAsset;
        GeoJSON?: StacAsset;
        // Pruned point source asset name in DPP produced STAC items
        growing_box_pruned?: StacAsset;
    };
    // Index in a Feature Collection
    index?: number;
    instrument?: string;
};

/**
 * Modified Feature Collection returned from STAC Search
 */
export interface StacSearchResponse<G extends Geometry = Geometry, P = GeoJsonProperties> {
    type: string;
    features?: StacFeature<G, P>[];
    links?: StacItemLink[];
    numberReturned?: number;
    context?: {
        limit?: number;
        returned?: number;
    };
    collection: STACCollection;
    id: string;
}

export type PointSourceStacResponse = StacSearchResponse & {
    id: string;
    collection?: string;
    /** the target specific id of the target that the point source belongs to */
    parentTargetId: string;
    /** STAC item ID of the target that the point source belongs to */
    relatedRasterItemId?: string;
    /** Metadata, which includes processor (algorithm) version */
    metadata?: { processor_version: string };
};

/** COG data returned from TiTiler at the stac/raster endpoint*/
export type TiTilerResponse = {
    band_names: string[];
    coordinates: number[];
    values: number[];
};

/**
 * Geometry the search should intersect with.
 */
export type StacSearchIntersect = {
    type: string;
    coordinates?: number[] | number[][];
    bbox?: number[];
};

/**
 * [minLng, minLat, maxLng, maxLat]
 * aka
 * [west, south, east, north]
 */
export type StacFormattedBbox = [number, number, number, number];

export type StacDateBlock = DateBlock<StacFeature<Point | Polygon>>;

export type StacTimeSelectorItem = TimeSelectorItem<StacFeature<Point | Polygon>>;
export type StacTimeSelectorData = StacTimeSelectorItem[];

/**
 * Filter language we're using in STAC search queries.
 */
export enum StacFilterLang {
    /** CQL2 JSON used for POST requests. */
    cql2Json = 'cql2-json',
    /** CQL2 Text used for GET requests. */
    cql2Text = 'cql2-text'
}

export enum DateRanges {
    Month = 'month',
    Quarter = 'quarter',
    Year = 'year'
}
export type CapturePlusCount = StacFeature &
    GeoJSON.Feature<
        GeoJSON.Polygon,
        CaptureFeatureProperties & {
            platformTimespanCaptureCount?: number;
        }
    > & { relatedRasterItemId?: string };
